import { Component, signal, inject } from '@angular/core';
import { LayoutService } from '@layout/service/app.layout.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { lastValueFrom } from 'rxjs';

import { AuthService } from '@core/services/auth/auth.service';
import { ToastService } from '@layout/service/toast.service';
import { EmailValidatorService } from '@shared/services';
import { Title } from '@angular/platform-browser';

interface Message {
  severity: 'success' | 'info' | 'warn' | 'error';
  content: string;
}

@Component({
  selector: 'cwa-forgot-password',
  templateUrl: 'forgot-password.component.html',
  standalone: false,
})
export class ForgotPasswordComponent {
  // private router = inject(Router);
  private fb = inject(FormBuilder);
  private layoutService = inject(LayoutService);
  private toast = inject(ToastService);
  private emailValidatorService = inject(EmailValidatorService);
  private authService = inject(AuthService);
  private titleService = inject(Title);

  formGroup: FormGroup;
  loading = false;
  showSuccessMessage = false;
  control = (name: string): FormControl => this.formGroup.get(name) as FormControl;
  messages = signal<Message[]>([]);

  constructor() {
    this.titleService.setTitle('Auth - Forgot Password | Cogwheel Analytics Tools');
    this.emailValidatorService.clearCache();

    this.formGroup = this.fb.group({
      email: [
        '',
        [Validators.required, Validators.email],
        // Commented emailExistsValidator and calling the email checking API because of not exposing the client's email publicly
        // [this.emailValidatorService.emailExistsValidator(true)],
      ],
    });
  }

  get dark(): boolean {
    return this.layoutService.config.colorScheme !== 'light';
  }

  async requestPasswordReset(): Promise<void> {
    // if (this.formGroup.invalid) return;
    if (!this.formGroup.valid || this.formGroup.invalid) {
      for (const name in this.formGroup.controls) {
        this.control(name)?.markAsTouched();
      }
      return;
    }
    try {
      const passwordResetRequest$ = this.authService.passwordResetRequest(this.formGroup.value);
      this.loading = true;
      await lastValueFrom(passwordResetRequest$);
      this.loading = false;
      this.messages.set([
        {
          severity: 'success',
          content:
            'If your email is registered with Cogwheel Analytics, you should receive a password reset email. Please check your inbox for further instructions.',
        },
      ]);
      this.showSuccessMessage = true;

      // await this.router.navigate(['/login']);
      this.emailValidatorService.clearCache();

      this.formGroup.reset();
    } catch (_error) {
      // Clean up UI state
      this.loading = false;
      this.showSuccessMessage = false;
      this.messages.set([]);
      // Don't show error here - let the interceptor handle it
      // The interceptor will show the proper error message from the API response
    }
  }

  onEmailInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const lowercaseValue = input.value.toLowerCase();
    if (input.value !== lowercaseValue) {
      this.formGroup.patchValue(
        {
          email: lowercaseValue,
        },
        { emitEvent: false }
      );
    }
  }
}
